import { IQuestionnaireQuestion } from '../Types/Products/IQuestionnaireQuestion';
import { FileData } from '../Components/Utils/FileItem/FileItem';

const INSUFFICIENT_EVIDENCE_PATTERNS = [
  /^$/i,
  /^n\/?a\.?$/i,
  /^none\.?$/i,
  /^not enough evidence/i,
  /^insufficient evidence/i,
  /^no (relevant )?evidence/i,
  /^cannot (be )?answer/i,
  /^unable to answer/i,
  /^evidence (is )?insufficient/i,
  /^skip(ped)?$/i,
];

/** True when the model indicated the evidence cannot support an answer. */
export function isInsufficientEvidenceAnswer(value: string | null | undefined): boolean {
  const trimmed = (value ?? '').trim();
  return INSUFFICIENT_EVIDENCE_PATTERNS.some((pattern) => pattern.test(trimmed));
}

/** True when leftover model output is JSON rather than a prose answer. */
export function looksLikeJsonAnswerPayload(value: string | null | undefined): boolean {
  const trimmed = (value ?? '').trim();
  if (!trimmed) {
    return false;
  }
  if (trimmed.startsWith('{') || trimmed.startsWith('[') || trimmed.startsWith('```')) {
    return true;
  }
  return /["']\d+["']\s*:/.test(trimmed);
}

export function parseAiAnswerPayload(raw: string): ParsedAiAnswerPayload | null {
  let text = raw.trim();
  const codeBlockMatch = text.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (codeBlockMatch) {
    text = codeBlockMatch[1].trim();
  }

  const candidates = extractJsonCandidates(text);
  if (candidates.length === 0) {
    return payloadFromObject(tryParseObject(text));
  }

  let best: ParsedAiAnswerPayload | null = null;
  let bestScore = -1;
  for (const candidate of candidates) {
    const payload = payloadFromObject(tryParseObject(candidate));
    if (!payload) {
      continue;
    }
    const score = scoreAnswerPayload(payload.answers);
    if (score > bestScore) {
      best = payload;
      bestScore = score;
    }
  }

  return best;
}

export function parseAiAnswersJson(raw: string): Record<string, string> | null {
  return parseAiAnswerPayload(raw)?.answers ?? null;
}

function extractJsonCandidates(text: string): string[] {
  const candidates: string[] = [];
  let i = 0;
  while (i < text.length) {
    if (text[i] !== '{') {
      i++;
      continue;
    }

    let depth = 0;
    let inString = false;
    let escape = false;
    const start = i;
    for (; i < text.length; i++) {
      const ch = text[i];
      if (inString) {
        if (escape) {
          escape = false;
          continue;
        }
        if (ch === '\\') {
          escape = true;
          continue;
        }
        if (ch === '"') {
          inString = false;
        }
        continue;
      }
      if (ch === '"') {
        inString = true;
        continue;
      }
      if (ch === '{') {
        depth++;
      } else if (ch === '}') {
        depth--;
        if (depth === 0) {
          candidates.push(text.slice(start, i + 1));
          i++;
          break;
        }
      }
    }

    if (depth !== 0) {
      candidates.push(text.slice(start));
      break;
    }
  }

  return candidates;
}

function tryParseObject(text: string): Record<string, unknown> | null {
  const parsed = parseJsonObject(text) ?? parseJsonObject(repairJsonObject(text));
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return null;
  }
  return parsed as Record<string, unknown>;
}

function parseJsonObject(text: string | null): unknown | null {
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function repairJsonObject(text: string): string | null {
  let value = text.trim();
  if (!value.startsWith('{')) {
    return null;
  }

  let inString = false;
  let escape = false;
  let depth = 0;
  for (const ch of value) {
    if (inString) {
      if (escape) {
        escape = false;
        continue;
      }
      if (ch === '\\') {
        escape = true;
        continue;
      }
      if (ch === '"') {
        inString = false;
      }
      continue;
    }
    if (ch === '"') {
      inString = true;
      continue;
    }
    if (ch === '{') {
      depth++;
    } else if (ch === '}') {
      depth--;
    }
  }

  if (inString) {
    value += '"';
  }
  value = value.replace(/,\s*$/, '');
  while (depth > 0) {
    value += '}';
    depth--;
  }

  return value;
}

const SKIP_REASON_META_KEYS = new Set(['skipReasons', 'skipped', 'reasons']);
const SOURCE_META_KEYS = new Set(['sources', 'evidenceSources']);

export const DEFAULT_AUTOFILL_SKIP_REASON =
  'No relevant evidence found in the selected files.';

export const PARSE_FALLBACK_SKIP_REASON =
  'Could not parse a separate draft for this question.';

export type ParsedAiAnswerPayload = {
  answers: Record<string, string>;
  skipReasons: Record<string, string>;
  sources: Record<string, string[]>;
};

function collectSkipReasons(
  value: unknown,
  into: Record<string, string>
): void {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return;
  }
  for (const [questionKey, reason] of Object.entries(value as Record<string, unknown>)) {
    const text = typeof reason === 'string' ? reason.trim() : '';
    if (text) {
      into[questionKey] = text;
    }
  }
}

function collectSources(
  value: unknown,
  into: Record<string, string[]>
): void {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return;
  }
  for (const [questionKey, raw] of Object.entries(value as Record<string, unknown>)) {
    const ids = Array.isArray(raw)
      ? raw.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
      : typeof raw === 'string' && raw.trim()
        ? [raw.trim()]
        : [];
    if (ids.length > 0) {
      into[questionKey] = ids.map((id) => id.trim());
    }
  }
}

function collectAnswerEntries(
  entries: Record<string, unknown>,
  answers: Record<string, string>,
  skipReasons: Record<string, string>,
  sources: Record<string, string[]>
): void {
  for (const [key, value] of Object.entries(entries)) {
    if (SKIP_REASON_META_KEYS.has(key)) {
      collectSkipReasons(value, skipReasons);
      continue;
    }

    if (SOURCE_META_KEYS.has(key)) {
      collectSources(value, sources);
      continue;
    }

    if (key === 'answers' && value && typeof value === 'object' && !Array.isArray(value)) {
      collectAnswerEntries(value as Record<string, unknown>, answers, skipReasons, sources);
      continue;
    }

    if (typeof value === 'string') {
      answers[key] = value;
    } else if (value === null || value === undefined) {
      answers[key] = '';
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      answers[key] = String(value);
    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
      const rec = value as Record<string, unknown>;
      answers[key] = typeof rec.answer === 'string' ? rec.answer : '';
      const reason =
        (typeof rec.skipReason === 'string' && rec.skipReason) ||
        (typeof rec.reason === 'string' && rec.reason) ||
        '';
      if (reason.trim()) {
        skipReasons[key] = reason.trim();
      }
      const nestedSources = rec.sources ?? rec.fileIds;
      if (Array.isArray(nestedSources)) {
        const ids = nestedSources.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
        if (ids.length > 0) {
          sources[key] = ids.map((id) => id.trim());
        }
      }
    }
  }
}

function payloadFromObject(obj: Record<string, unknown> | null): ParsedAiAnswerPayload | null {
  if (!obj) {
    return null;
  }

  const answers: Record<string, string> = {};
  const skipReasons: Record<string, string> = {};
  const sources: Record<string, string[]> = {};
  collectAnswerEntries(obj, answers, skipReasons, sources);

  return Object.keys(answers).length > 0 ? { answers, skipReasons, sources } : null;
}

function scoreAnswerPayload(answers: Record<string, string>): number {
  const numbered = Object.keys(answers).filter((key) => /^\d+$/.test(key)).length;
  const nonEmpty = Object.values(answers).filter((value) => value.trim().length > 0).length;
  return numbered * 10 + nonEmpty;
}

/** Must match MaxFilesPerBatch in Mesma.NewGen.McpServer/LibraryTools.cs. */
export const MAX_FILES_PER_RETRIEVE = 25;

export interface BuildGenerateAnswersPromptParams {
  areaId: string;
  criteriaId: string;
  criteriaTitle: string;
  sectionTitle: string;
  questions: IQuestionnaireQuestion[];
  evidenceFiles: FileData[];
}

function chunkEvidenceFiles(files: FileData[], size: number): FileData[][] {
  const batches: FileData[][] = [];
  for (let i = 0; i < files.length; i += size) {
    batches.push(files.slice(i, i + size));
  }
  return batches;
}

function formatEvidenceFile(file: FileData): string {
  return `- ${file.fileName} (id: ${file.id})`;
}

function formatEvidenceInstructions(evidenceFiles: FileData[]): string {
  if (evidenceFiles.length <= MAX_FILES_PER_RETRIEVE) {
    const evidenceLines = evidenceFiles.map(formatEvidenceFile).join('\n');
    return `Using the following linked evidence files (ids may refer to library files or inline-uploaded files). Read them with ONE retrieve_library_files_content call that includes every file id below (the server loads them in parallel). Do not call the tool once per file:
${evidenceLines}`;
  }

  const batches = chunkEvidenceFiles(evidenceFiles, MAX_FILES_PER_RETRIEVE);
  const batchBlocks = batches
    .map((batch, index) => {
      const lines = batch.map(formatEvidenceFile).join('\n');
      return `Batch ${index + 1} of ${batches.length}:\n${lines}`;
    })
    .join('\n\n');

  return `Using the following ${evidenceFiles.length} linked evidence files, split into ${batches.length} batches. The file reader accepts at most ${MAX_FILES_PER_RETRIEVE} fileIds per call, so make exactly one retrieve_library_files_content call per batch, passing every id in that batch. You may issue the batch calls in parallel. Do not call the tool once per file, and do not merge batches into one call. Read ALL batches before drafting any answer.

${batchBlocks}`;
}

export function buildGenerateAnswersPrompt({
  areaId,
  criteriaId,
  criteriaTitle,
  sectionTitle,
  questions,
  evidenceFiles,
}: BuildGenerateAnswersPromptParams): string {
  const evidenceInstructions = formatEvidenceInstructions(evidenceFiles);

  const questionLines = questions
    .map((question, index) => `${index + 1}. ${question.question}`)
    .join('\n');

  const questionCount = questions.length;

  return `You are helping complete a diagnostic questionnaire for the criteria: "${criteriaTitle}".
Section: "${sectionTitle}"

This is the unvalidated / data-gathering stage (same as coaching drafts). Monitoring notes, working notes, and other informal notes are valid draft sources when they address a question.

FIRST, call get_diagnostic_questionnaire_answers with areaId "${areaId}" and criteriaId "${criteriaId}" to load what the user has already written. Optionally call get_diagnostic_criteria_context with the same areaId and criteriaId for criteria guidance. Use those tools with the ids from this request — do not ask the user for ids.

${evidenceInstructions}

For each diagnostic question below, draft a concise answer when the evidence files contain relevant information that addresses it.

Rules:
- Base every answer strictly on the retrieved evidence. Do not invent, guess, or pad with generic QA wording beyond what the files support.
- Treat monitoring notes, working notes, lists, and informal note style as legitimate sources — paraphrase them into concise drafts when they clearly address the question. Do not leave a question blank merely because the source is informal, incomplete as a polished policy, or not an evaluative summary.
- If a question is clearly addressed by the notes or files, draft an answer. Do not prefer blank out of caution when relevant content is present.
- If the evidence is missing, irrelevant, or does not address a question at all, set that question's value to an empty string "".
- Prefer leaving a question blank only when the files genuinely do not support an answer — not when they only lack polish.
- Do not write phrases like "insufficient evidence" or "N/A" in the answer value — use "" instead.
- For every blank question, add a short skipReasons entry explaining why the selected files could not support an answer. Be specific (for example, the files do not mention this topic). Do not invent evidence.
- For every non-empty answer, list the file ids from the evidence list above that actually supported it in a sources map. Only include ids that appear in the evidence list. Omit the key when no files applied.

Return ONLY a JSON object with exactly ${questionCount} numbered keys ("1" through "${questionCount}") plus a skipReasons object for any blanks and a sources object for drafted answers:
{
  "1": "<draft or empty string>",
  "2": "",
  "skipReasons": {
    "2": "<one short sentence>"
  },
  "sources": {
    "1": ["<file id>"]
  }
}
Include every numbered key. Use a non-empty string only for questions the evidence can support.
Do not include markdown, commentary, or any extra JSON before or after that object.

Questions:
${questionLines}`;
}

export function mapAiAnswersToQuestions(
  questions: IQuestionnaireQuestion[],
  parsed: Record<string, string>,
  skipReasons: Record<string, string> = {},
  sources: Record<string, string[]> = {}
): {
  answersByQuestionId: Record<string, string>;
  answeredQuestionIds: string[];
  skipReasonsByQuestionId: Record<string, string>;
  sourceIdsByQuestionId: Record<string, string[]>;
} {
  const answersByQuestionId: Record<string, string> = {};
  const answeredQuestionIds: string[] = [];
  const skipReasonsByQuestionId: Record<string, string> = {};
  const sourceIdsByQuestionId: Record<string, string[]> = {};

  questions.forEach((question, index) => {
    const numberKey = String(index + 1);
    const answerText = parsed[numberKey]?.trim() ?? '';
    if (!isInsufficientEvidenceAnswer(answerText)) {
      answersByQuestionId[question.id] = answerText;
      answeredQuestionIds.push(question.id);
      const ids = sources[numberKey] ?? sources[question.id] ?? [];
      if (ids.length > 0) {
        sourceIdsByQuestionId[question.id] = ids;
      }
      return;
    }

    const reason =
      skipReasons[numberKey]?.trim() ||
      skipReasons[question.id]?.trim() ||
      DEFAULT_AUTOFILL_SKIP_REASON;
    skipReasonsByQuestionId[question.id] = reason;
  });

  return { answersByQuestionId, answeredQuestionIds, skipReasonsByQuestionId, sourceIdsByQuestionId };
}

export function snapshotQuestionAnswers(
  questions: IQuestionnaireQuestion[]
): Record<string, string> {
  return questions.reduce(
    (acc, question) => {
      acc[question.id] = question.answer;
      return acc;
    },
    {} as Record<string, string>
  );
}
